import React from "react";
import { ArrowRight, Phone } from "lucide-react";
import { FIRM } from "../data/mock";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen pt-32 lg:pt-40 pb-20 overflow-hidden bg-ivory"
    >
      {/* Decorative background pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #102845 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
      </div>

      {/* Soft gold orb */}
      <div className="absolute top-1/4 -right-40 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left: Text */}
        <div className="lg:col-span-7 z-10">
          <div className="flex items-center gap-3 mb-8 fade-up">
            <div className="gold-line" />
            <span
              data-testid="hero-eyebrow"
              className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium"
            >
              Forensic Accounting · Trial Law · Texas
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] text-navy tracking-tight fade-up-delay-1"
          >
            Let us handle
            <br />
            your case with
            <br />
            <span className="italic text-gold">confidence.</span>
          </h1>

          <p
            data-testid="hero-subhead"
            className="mt-8 text-lg lg:text-xl text-navy/75 max-w-xl leading-relaxed fade-up-delay-2"
          >
            For more than 50 years, Richard L. Bufkin — attorney, CPA, and
            AICPA-certified forensic accountant — has handled the cases other
            lawyers struggle to read. Business litigation, fraud, bankruptcy,
            economic damages.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up-delay-3">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="btn-primary inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-medium group"
            >
              Schedule a Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={`tel:${FIRM.phone.replace(/[^\d+]/g, "")}`}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 px-2 py-4 text-sm text-navy font-medium hover:text-gold transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={2} />
              <span className="link-underline">{FIRM.phone}</span>
            </a>
          </div>

          {/* Credentials inline */}
          <div className="mt-14 pt-8 border-t border-[#C0974E]/20 grid grid-cols-2 sm:grid-cols-4 gap-6 fade-up-delay-4">
            <div data-testid="stat-years">
              <div className="font-serif text-4xl text-navy">50+</div>
              <div className="text-xs uppercase tracking-wider text-navy/60 mt-1">
                Years Practicing
              </div>
            </div>
            <div data-testid="stat-cpa">
              <div className="font-serif text-4xl text-navy">CPA</div>
              <div className="text-xs uppercase tracking-wider text-navy/60 mt-1">
                Certified Public Accountant
              </div>
            </div>
            <div data-testid="stat-cff">
              <div className="font-serif text-4xl text-navy">CFF</div>
              <div className="text-xs uppercase tracking-wider text-navy/60 mt-1">
                Certified in Financial Forensics
              </div>
            </div>
            <div data-testid="stat-llm">
              <div className="font-serif text-4xl text-navy">
                LL.M.<span className="text-gold">.</span>
              </div>
              <div className="text-xs uppercase tracking-wider text-navy/60 mt-1">
                Master of Laws, Taxation
              </div>
            </div>
          </div>
        </div>

        {/* Right: Image */}
        <div className="lg:col-span-5 relative z-10 fade-up-delay-2">
          <div className="relative aspect-[4/5] max-w-md mx-auto">
            {/* Gold frame accent */}
            <div className="absolute -top-4 -right-4 w-full h-full border-2 border-gold/40 rounded-sm" />
            {/* Portrait container with navy background */}
            <div className="relative w-full h-full overflow-hidden rounded-sm shadow-2xl bg-navy-deep">
              <img
                src="https://customer-assets.emergentagent.com/job_legal-finance-3/artifacts/wh5zrplf_No-Background-4.png"
                alt="Richard L. Bufkin — Attorney, CPA, and AICPA-certified Forensic Accountant"
                data-testid="hero-image"
                className="w-full h-full object-cover object-top"
              />
              {/* Soft top vignette to blend with navy background */}
              <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/40 via-transparent to-navy-deep/20 pointer-events-none" />
            </div>
            {/* Caption card */}
            <div className="absolute -bottom-6 -left-6 bg-navy text-ivory px-6 py-4 max-w-[260px] shadow-xl border-l-2 border-gold">
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-1.5 font-medium">
                Richard L. Bufkin
              </div>
              <div className="font-serif text-sm leading-snug text-ivory">
                Attorney · CPA · Forensic Accountant
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-navy/40">
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-navy/40 to-transparent" />
      </div>
    </section>
  );
};
